<script setup lang="ts">
import { computed, type Ref } from 'vue';
import PageGrantCheckbox from '@/components/permissions/PageGrantCheckbox.vue';
import AbilityMultiSelect from '@/components/permissions/AbilityMultiSelect.vue';
import type { AbilityLevel } from '@/components/permissions/abilityFlagKey';
import {
  usePages,
  useUserPageState,
  useSetUserPageGrant,
} from '@/composables/api/usePagesApi';
import { usePermissions } from '@/composables/usePermissions';
import type { PageDefinition } from '@/types/types';

/**
 * The "Pages" tab body. For the selected user, lists every seeded page with:
 *  - a single "Enabled" toggle reflecting / driving the per-user page grant
 *    (nav-bar button + route access), and
 *  - subrows for the models surfaced under that page, each with a cumulative
 *    ability dropdown.
 *
 * The page grant and the model permissions are INDEPENDENT: toggling "Enabled"
 * only writes user_page_grants; the model dropdowns edit user_model_permissions
 * — the very same rows the "By model" tab edits (shared state passed down from
 * PermissionsPage), so a change here is reflected there and vice versa.
 */

const props = defineProps<{
  selectedUserId: number | null;
  /** Shared model-permission editing state + handlers, owned by PermissionsPage. */
  permissionState: Record<string, Record<string, boolean>>;
  maxGrantableLevelForModel: (modelKey: string) => AbilityLevel;
  setAbilityLevelForModel: (modelKey: string, level: AbilityLevel) => void;
}>();

const { data: pagesData, isLoading: pagesLoading } = usePages();

const userIdRef = computed(() => props.selectedUserId) as Ref<number | null>;
const { data: userPagesData, isLoading: userPagesLoading } = useUserPageState(userIdRef);

const { mutate: setGrant, isPending: settingGrant } = useSetUserPageGrant();

const { canAccessPage } = usePermissions();

/**
 * Privilege bounding: a non-developer may only toggle a page they themselves
 * hold (you can't grant access you don't have). Developers bypass via
 * canAccessPage. Mirrors the backend guard in PageController::setUserPage.
 */
function canManagePage(pageName: string): boolean {
  return canAccessPage(pageName);
}

const isLoading = computed(() => pagesLoading.value || userPagesLoading.value);

const sortedPages = computed<PageDefinition[]>(() =>
  [...(pagesData.value ?? [])].sort((a, b) => a.sort_order - b.sort_order),
);

/**
 * The API returns each page's models in pivot sort_order (seeder insertion
 * order); alphabetical is what makes a model findable in the list.
 */
function sortedModelsForPage(page: PageDefinition): PageDefinition['models'] {
  return [...page.models].sort((modelA, modelB) =>
    modelA.display_name.localeCompare(modelB.display_name, undefined, { sensitivity: 'base' })
  );
}

const grantedByPageId = computed<Record<number, boolean>>(() => {
  const out: Record<number, boolean> = {};
  for (const row of userPagesData.value ?? []) {
    out[row.page_id] = row.granted;
  }
  return out;
});

function isGranted(pageId: number): boolean {
  return grantedByPageId.value[pageId] ?? false;
}

/**
 * The page's model dropdowns are disabled until the page is Enabled for the
 * selected user. The model permissions are still independent data, but gating
 * the controls behind the page toggle signals that the two belong together on
 * this surface. (They remain editable on By-model.)
 */
function modelsDisabledForPage(pageId: number): boolean {
  return props.selectedUserId === null || !isGranted(pageId);
}

function toggleGranted(pageId: number, pageName: string, value: boolean) {
  if (props.selectedUserId === null) return;
  // Defensive guard matching the disabled state — a non-developer can't grant
  // a page they don't hold.
  if (!canManagePage(pageName)) return;
  setGrant({
    userId: props.selectedUserId,
    pageId,
    granted: value,
  });
}
</script>

<template>
  <!-- No Card of its own: the parent PermissionsPage hosts this inside the
       shared tabbed card, whose header (the tab bar) replaces the old title. -->
  <div class="flex h-full min-h-0 flex-col">
      <div
        class="min-h-0 flex-1 space-y-2 overflow-y-auto px-6 pb-6 max-md:pb-mobile-area-bar-clearance"
        data-testid="permissions-pages-list"
      >
        <p v-if="isLoading" class="text-muted-foreground text-sm">Loading pages&hellip;</p>
        <p
          v-else-if="sortedPages.length === 0"
          class="text-muted-foreground text-sm"
          data-testid="permissions-no-pages"
        >
          No pages defined.
        </p>
        <template v-else>
          <article
            v-for="page in sortedPages"
            :key="page.id"
            class="rounded-lg border bg-card p-4"
            :data-testid="`permissions-page-row-${page.id}`"
          >
            <div class="flex flex-row items-center justify-between gap-3">
              <h4
                class="text-foreground text-base font-medium"
                :data-testid="`permissions-page-${page.id}-name`"
              >{{ page.name }}</h4>
              <PageGrantCheckbox
                :page-id="page.id"
                :granted="isGranted(page.id)"
                :disabled="settingGrant || selectedUserId === null || !canManagePage(page.name)"
                :selected-user-id="selectedUserId"
                @change="(value) => toggleGranted(page.id, page.name, value)"
              />
            </div>

            <!-- Model subrows: the models used within this page. The dropdowns
                 edit user_model_permissions (shared with the By model tab).
                 They're disabled until the page is Enabled above — a UX hint
                 that the page and its models belong together here. -->
            <div
              v-if="page.models.length > 0"
              class="mt-3 space-y-1 border-t pt-3"
              :data-testid="`permissions-page-${page.id}-models`"
            >
              <span class="text-muted-foreground block pb-1 text-xs font-medium uppercase tracking-wide">
                Models
              </span>
              <div
                v-for="model in sortedModelsForPage(page)"
                :key="model.key"
                class="flex items-center justify-between gap-3 py-1.5"
                :data-testid="`permissions-page-${page.id}-model-${model.key}`"
              >
                <span class="text-foreground/90 text-sm">{{ model.display_name }}</span>
                <AbilityMultiSelect
                  :flags="permissionState[model.key] ?? {}"
                  :max-level="maxGrantableLevelForModel(model.key)"
                  :disabled="modelsDisabledForPage(page.id)"
                  :test-id-prefix="`permissions-page-${page.id}-model-${model.key}-abilities`"
                  :aria-label="`Abilities for ${model.display_name}`"
                  :trigger-class="'w-44 justify-between'"
                  @set-level="(level) => setAbilityLevelForModel(model.key, level)"
                />
              </div>
            </div>
          </article>
        </template>
      </div>
  </div>
</template>
